import { useState } from "react";
import { useGetLocale, useTranslate } from "@refinedev/core";
import { Flex, Typography } from "antd";
import { PlayIcon } from "@phosphor-icons/react";
import { Button, Checkbox, Modal, radius } from "@libertify/ui";
import styled from "styled-components";
import { useTeacherOnboarding } from "./teacherOnboardingContext";
import { I18N_PREFIX, SKEMA_VIDEO_URL, TITLE_FONT } from "./constants";

const WelcomeStep = () => {
  const t = useTranslate();
  const getLocale = useGetLocale();
  const { goForward } = useTeacherOnboarding();
  // Consent is a local gate: the user can't leave this step until it's ticked.
  // Being on any later step therefore implies consent (no global flag needed).
  const [consented, setConsented] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const locale = getLocale() ?? "en";
  // The #t fragment makes the browser show a frame as the poster/thumbnail.
  const videoUrl = `${SKEMA_VIDEO_URL[locale] ?? SKEMA_VIDEO_URL.en}#t=0.5`;
  const captionTrack = (
    <track kind="captions" srcLang="en" src="data:text/vtt,WEBVTT%0A%0A" />
  );

  return (
    <StyledWelcome>
      <StyledVideoThumb
        type="button"
        onClick={() => setIsVideoOpen(true)}
        aria-label={t(`${I18N_PREFIX}.welcome.video.aria`)}
      >
        <video src={videoUrl} preload="metadata" muted playsInline>
          {captionTrack}
        </video>
        <StyledPlayBadge>
          <StyledPlayDisc>
            <PlayIcon size={20} weight="fill" />
          </StyledPlayDisc>
        </StyledPlayBadge>
      </StyledVideoThumb>

      <Flex vertical align="flex-start" gap={14}>
        <div aria-hidden style={{ fontSize: 32, lineHeight: 1 }}>
          🧬
        </div>
        <Kicker>{t(`${I18N_PREFIX}.welcome.kicker`)}</Kicker>

        <StyledTitle level={2} style={{ fontFamily: TITLE_FONT }}>
          {t(`${I18N_PREFIX}.welcome.title`)}
        </StyledTitle>

        <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
          {t(`${I18N_PREFIX}.welcome.tagline`)}
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
          {t(`${I18N_PREFIX}.welcome.description`)}
        </Typography.Paragraph>

        <StyledConsent
          checked={consented}
          onChange={(e) => setConsented(e.target.checked)}
        >
          {t(`${I18N_PREFIX}.welcome.consent`)}
        </StyledConsent>

        <Flex justify="flex-end" style={{ width: "100%" }}>
          <Button
            variant="primary"
            size="l"
            disabled={!consented}
            onClick={goForward}
          >
            {t(`${I18N_PREFIX}.welcome.start`)}
          </Button>
        </Flex>
      </Flex>

      <Modal
        open={isVideoOpen}
        title={t(`${I18N_PREFIX}.welcome.video.title`)}
        size="large"
        showFooter={false}
        onClose={() => setIsVideoOpen(false)}
        // The Moodle LTI frame is ~766px square, narrower than the 800px
        // "large" modal, so cap the width to the frame.
        style={{ maxWidth: "calc(100vw - 32px)" }}
        styles={{ body: { padding: 0, lineHeight: 0 } }}
      >
        {/* Mounted only while open: the intro preloads nothing, and removing
            the element on close pauses playback (per the HTML media spec). */}
        {isVideoOpen ? (
          <StyledModalVideo src={videoUrl} controls autoPlay playsInline>
            {captionTrack}
          </StyledModalVideo>
        ) : null}
      </Modal>
    </StyledWelcome>
  );
};

export default WelcomeStep;

// ─── Styled components ───

// Narrow video column on the left, copy on the right, so the whole step fits
// the Moodle LTI iframe (~766px square) without scrolling.
const StyledWelcome = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 240px) minmax(0, 1fr);
  align-items: center;
  gap: 40px;
  width: 100%;

  @media (width <= 720px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 20px;
  }
`;

const StyledPlayBadge = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(0 0 0 / 18%);
  transition: background 0.2s ease;
`;

// No border: the intro has to read as part of the Moodle page, not a boxed embed.
const StyledVideoThumb = styled.button`
  position: relative;
  display: block;
  width: 100%;
  max-width: 240px;
  padding: 0;
  border: 0;
  border-radius: ${radius.box}px;
  overflow: hidden;
  background: #000;
  aspect-ratio: 4 / 3;
  cursor: pointer;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    pointer-events: none;
  }

  &:hover ${StyledPlayBadge} {
    background: rgb(0 0 0 / 32%);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colorPrimary};
    outline-offset: 3px;
  }

  @media (width <= 720px) {
    max-width: 180px;
  }
`;

const StyledPlayDisc = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: ${radius.circle};
  background: rgb(255 255 255 / 92%);
  color: #111;
`;

// 60vh leaves room for the modal header + top offset inside the square frame.
const StyledModalVideo = styled.video`
  display: block;
  width: 100%;
  max-height: 60vh;
  background: #000;
`;

const StyledTitle = styled(Typography.Title)`
  && {
    margin: 0;
    font-size: clamp(26px, 2.4vw, 40px);
    line-height: 1.15;
  }
`;

const Kicker = styled.div`
  font-size: 11px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  font-weight: 700;
  color: ${({ theme }) => theme.colorPrimary};
`;

// Top-align the box with the first line of the wrapped label.
const StyledConsent = styled(Checkbox)`
  && {
    display: inline-flex;
    align-items: flex-start;
    text-align: left;
  }

  .ant-checkbox {
    margin-top: 2px;
  }
`;
